import { useEffect, useState } from 'react';
import { Page, Spinner, Banner, BlockStack, Card, Text } from '@shopify/polaris';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SpendUnlockGiftWizard } from './campaigns/SpendUnlockGiftWizard';
import { BuyXGetYWizard } from './campaigns/BuyXGetYWizard';
import { MilestoneRewardsWizard } from './campaigns/MilestoneRewardsWizard';
import { GenericCampaignEditor } from './campaigns/GenericCampaignEditor';
import { useApi } from '../providers/ApiProvider';
import {
  resolveCampaignEditor,
  type CampaignEditorKey,
  type CampaignLike,
} from '../lib/campaignEditor';
import { t } from '../i18n';

/**
 * Routes to the campaign-type-specific editor (create or edit).
 * Create: /campaigns/new/configure?preset=…
 * Edit:   /campaigns/:id  (loads campaign → opens matching wizard)
 */
export function CampaignEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();

  const queryPreset = (params.get('preset') || '') as CampaignEditorKey | '';
  const isCreateRoute = !id;

  const [editorKey, setEditorKey] = useState<CampaignEditorKey | null>(
    queryPreset || (isCreateRoute ? 'generic' : null),
  );
  const [loading, setLoading] = useState(!isCreateRoute && !queryPreset);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Create flow — trust query preset
    if (isCreateRoute) {
      setEditorKey(queryPreset || 'generic');
      setLoading(false);
      return;
    }

    // Edit with preset hint (from list click) — still verify against API when possible
    if (queryPreset && PRESET_KEYS.has(queryPreset)) {
      setEditorKey(queryPreset);
      setLoading(false);
      // Soft refresh resolution in background (tags may differ)
      void api
        .get<CampaignLike>(`/campaigns/${id}`)
        .then((c) => {
          const resolved = resolveCampaignEditor(c);
          if (resolved !== queryPreset) setEditorKey(resolved);
        })
        .catch(() => undefined);
      return;
    }

    setLoading(true);
    setError(null);
    api
      .get<CampaignLike>(`/campaigns/${id}`)
      .then((c) => {
        setEditorKey(resolveCampaignEditor(c));
      })
      .catch((e: Error) => {
        setError(e.message || 'Failed to load campaign');
        setEditorKey('generic');
      })
      .finally(() => setLoading(false));
  }, [api, id, isCreateRoute, queryPreset]);

  if (loading || !editorKey) {
    return (
      <Page title={t("Loading campaign…")} backAction={{ content: t("Campaigns"), onAction: () => navigate('/campaigns') }}>
        <Card>
          <BlockStack gap="300" inlineAlign="center">
            <Spinner accessibilityLabel={t("Loading campaign editor")} size="large" />
            <Text as="p" tone="subdued">
              {t("Opening the right editor for this campaign…")}
            </Text>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (error && editorKey === 'generic') {
    return (
      <Page title={t("Campaign")} backAction={{ content: t("Campaigns"), onAction: () => navigate('/campaigns') }}>
        <Banner tone="critical" title={t("Could not open campaign")}>
          <p>{error}</p>
        </Banner>
      </Page>
    );
  }

  if (editorKey === 'spend_unlock_gift' || editorKey === 'quantity_unlock_gift') {
    return <SpendUnlockGiftWizard editorPreset={editorKey} />;
  }

  if (editorKey === 'buy_x_get_y') {
    return <BuyXGetYWizard />;
  }

  if (editorKey === 'milestone_rewards') {
    return <MilestoneRewardsWizard />;
  }

  return <GenericCampaignEditor />;
}

const PRESET_KEYS = new Set<string>([
  'spend_unlock_gift',
  'quantity_unlock_gift',
  'buy_x_get_y',
  'milestone_rewards',
]);
